import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLightboxLike } from "../shared/useLightboxLike";
import { useModelDisplayName } from "../shared/useModelDisplayName";
import { DATE_FORMAT_OPTIONS } from "../shared/constants";
import { getResizedImageUrl, GRID_THUMBNAIL_WIDTH, GRID_THUMBNAIL_QUALITY } from "@/lib/imageUrl";

interface UseImageLightboxOptions {
  imageUrl: string;
  referenceImageUrl?: string;
  createdAt?: string;
  model?: string | null;
  onClose: () => void;
  onUseSourceImage?: () => void;
  navigation?: {
    onNext: () => void;
    onPrev: () => void;
  };
}

export function useImageLightbox({
  imageUrl,
  referenceImageUrl,
  createdAt,
  model,
  onClose,
  onUseSourceImage,
  navigation,
}: UseImageLightboxOptions) {
  const navigate = useNavigate();
  const { t, locale } = useLanguage();

  const { liked, handleToggleLike } = useLightboxLike({
    mediaUrl: imageUrl,
    mediaType: "image",
    thumbnailUrl: getResizedImageUrl(imageUrl, GRID_THUMBNAIL_WIDTH, { quality: GRID_THUMBNAIL_QUALITY }),
    referenceImageUrl,
  });

  const modelDisplayName = useModelDisplayName(model, "image");

  // Keyboard arrow navigation
  useEffect(() => {
    if (!navigation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        navigation.onNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        navigation.onPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigation]);

  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString(locale, DATE_FORMAT_OPTIONS)
    : new Date().toLocaleDateString(locale, DATE_FORMAT_OPTIONS);

  const handleEdit = () => {
    onClose();
    navigate("/generate-image", { state: { imageUrl } });
  };

  const handleAnimate = () => {
    onClose();
    navigate("/text-to-video", { state: { animateImage: imageUrl } });
  };

  const handleUseSource = () => {
    if (onUseSourceImage) {
      onUseSourceImage();
    } else if (referenceImageUrl) {
      onClose();
      navigate("/generate-image", { state: { imageUrl: referenceImageUrl } });
    }
  };

  return {
    t,
    liked,
    formattedDate,
    modelDisplayName,
    handleToggleLike,
    handleEdit,
    handleAnimate,
    handleUseSource,
  };
}
